/**
 * Pure lot/serial traceability + FEFO logic (CC-6: NEVER use Lingui
 * macros here).
 *
 * A product can be tracked by:
 *   - 'none'   : without lot (F1–F3 behavior).
 *   - 'lot'    : by lot number (multiple units per lot).
 *   - 'serial' : by serial number (1 unit per serial).
 *
 * When a product is tracked, receipts/deliveries/adjustments REQUIRE a
 * lot and on-hand is maintained per (product × location × lot).
 *
 * FEFO (First-Expired-First-Out): when performing an outbound of a tracked product,
 * the available lot expiring FIRST is suggested/auto-selected. Deterministic:
 *   1) only lots with on-hand > 0 in the source location;
 *   2) sorted by ascending expiry date (those expiring earlier leave
 *      first); lots WITHOUT expiry go to the end (they never expire);
 *   3) stable tiebreaker by name and then by id, for reproducible results.
 *
 * Quantities in INTEGERS (minor units, scale factor 1000), same as F1–F3.
 */

export type ProductTracking = "none" | "lot" | "serial";

export const PRODUCT_TRACKINGS: readonly ProductTracking[] = ["none", "lot", "serial"] as const;

export function isProductTracking(value: string): value is ProductTracking {
  return (PRODUCT_TRACKINGS as readonly string[]).includes(value);
}

/** Normalizes a string (possibly null) to a valid ProductTracking. */
export function coerceProductTracking(value: string | null | undefined): ProductTracking {
  return value && isProductTracking(value) ? value : "none";
}

/** Does the product require a lot in its movements? (tracked by lot or serial) */
export function isTracked(tracking: string | null | undefined): boolean {
  return coerceProductTracking(tracking) !== "none";
}

/** Lot (relevant subset for FEFO / expiry). */
export interface LotLike {
  id: string;
  name: string | null;
  /** ISO expiry date (YYYY-MM-DD) or null if it does not expire. */
  expiry_date: string | null;
}

/** Availability of a lot in a location (on-hand per lot). */
export interface LotAvailability {
  lot: LotLike;
  /** Lot on-hand in source location (minor units, >= 0). */
  onHand: number;
}

/**
 * Sorts lots by FEFO (expires first → leaves first). Lots without expiry
 * (expiry_date null) go TO THE END. Stable total order (name→id tiebreak)
 * so the result is deterministic. Does not mutate input.
 */
export function sortLotsFefo(lots: LotLike[]): LotLike[] {
  return lots.toSorted(compareFefo);
}

/** Deterministic FEFO comparator between two lots. */
function compareFefo(a: LotLike, b: LotLike): number {
  const ea = a.expiry_date;
  const eb = b.expiry_date;
  if (ea !== eb) {
    // null = never expires → to the end.
    if (!ea) {
      return 1;
    }
    if (!eb) {
      return -1;
    }
    return ea < eb ? -1 : 1;
  }
  // Same expiry (or both null): stable tiebreaker by name and then id.
  const na = a.name ?? "";
  const nb = b.name ?? "";
  if (na !== nb) {
    return na < nb ? -1 : 1;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Selects the lot expiring first AMONG those with available on-hand in
 * the location. Returns null if no lot has stock. Basis of delivery
 * FEFO auto-selection.
 */
export function suggestFefoLot(available: LotAvailability[]): LotLike | null {
  const withStock = available.filter((a) => a.onHand > 0);
  if (withStock.length === 0) {
    return null;
  }
  const sorted = withStock.toSorted((a, b) => compareFefo(a.lot, b.lot));
  return sorted[0]!.lot;
}

/** A portion of an outbound allocated to a specific lot. */
export interface FefoAllocation {
  lotId: string;
  /** Quantity taken from this lot (minor units, > 0). */
  qty: number;
}

/**
 * Distributes an outbound quantity among available lots following FEFO:
 * exhausts the lot expiring earlier, then the next, etc. Deterministic. If
 * demand exceeds total on-hand, returns `shortfall` > 0 (uncovered shortage)
 * and allocates only what is available.
 */
export function allocateFefo(
  available: LotAvailability[],
  demand: number,
): { allocations: FefoAllocation[]; shortfall: number } {
  if (demand <= 0) {
    return { allocations: [], shortfall: 0 };
  }
  const sorted = available
    .filter((a) => a.onHand > 0)
    .toSorted((a, b) => compareFefo(a.lot, b.lot));
  const allocations: FefoAllocation[] = [];
  let remaining = demand;
  for (const entry of sorted) {
    if (remaining <= 0) {
      break;
    }
    const take = Math.min(entry.onHand, remaining);
    allocations.push({ lotId: entry.lot.id, qty: take });
    remaining -= take;
  }
  return { allocations, shortfall: Math.max(remaining, 0) };
}
